import { useHistory, useParams } from 'react-router-dom';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  DigitalIdentityTrustStatus,
  EstateStatus,
  EstateTaskIdentifier,
  EstateTaskInstance,
  EstateTaskInstanceStatus,
  User,
} from 'queries';

import { AssignButton, Button } from 'common/button';
import { EstateDashboardTab } from 'pages/estate-dashboard/estate-dashboard';
import { EstateStatusLabel, resultsAreReady } from 'utils/estate';

export interface StyleProps {
  background?: string;
  border?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useStyles = makeStyles<Theme, any>(() => ({
  estateButton: {
    background: ({ background }) => background,
    '&:hover': {
      background: ({ background }: StyleProps) => background,
      filter: 'brightness(70%)',
    },
    backgroundColor: '#1C2541',
    color: 'white',
    fontWeight: 400,
    borderRadius: '100px',
    padding: '8px 16px',
    width: '76px',
    height: '24px',
    whiteSpace: 'nowrap',
    minWidth: 'max-content',
  },
  estateIcon: {
    paddingRight: '10px',
  },
}));

export interface ButtonProps {
  estateTaskInstance?: EstateTaskInstance;
  status: EstateStatus;
  locked?: boolean;
  loggedInUser?: Partial<User>;
  launchEditModalFn: () => void;
  onAssignClick?: (task: EstateTaskInstance) => void;
}

const EstateStatusButton = ({
  estateTaskInstance,
  status,
  locked,
  loggedInUser,
  launchEditModalFn,
  onAssignClick,
}: ButtonProps) => {
  const history = useHistory();
  const { portalId, estateId } = useParams<Record<string, string>>();

  const props: StyleProps = {
    background: EstateStatusLabel[status].background,
    border: EstateStatusLabel[status].border,
  };
  const classes = useStyles(props);

  // only certain estate status have clickable actions
  let enabledStatusButton = [
    EstateStatus.DetailEntry,
    EstateStatus.DocumentUpload,
    EstateStatus.Payment,
    EstateStatus.ResultsReady,
    EstateStatus.Completed,
  ].find((item) => item === status)
    ? true
    : false;

  // disabled the button if results are ready but the user is not DIT approved
  if (
    resultsAreReady(status) &&
    loggedInUser?.efxDigitalIdentityTrust !== DigitalIdentityTrustStatus.Approve
  ) {
    enabledStatusButton = false;
  }

  const handleEstateButtonClick = () => {
    if (resultsAreReady(status)) {
      history.push(
        `/home/${portalId}/estate/${estateId}/estate-dashboard?tab=${EstateDashboardTab.SEARCH_RESULTS}`,
      );
      return;
    }

    launchEditModalFn();
  };

  if (
    !estateTaskInstance ||
    estateTaskInstance?.identifier === EstateTaskIdentifier.TrusuiteSearch
  ) {
    return (
      <Button
        className={classes.estateButton}
        color="primary"
        disabled={!enabledStatusButton}
        onClick={handleEstateButtonClick}
        size="small"
      >
        <span>
          {EstateStatusLabel[status].icon && (
            <img
              className={classes.estateIcon}
              src={'/' + EstateStatusLabel[status].icon + '.svg'}
              alt={EstateStatusLabel[status].label}
            />
          )}

          {EstateStatusLabel[status].label}
        </span>
      </Button>
    );
  } else if (
    // Show a disabled button if the task has an assign button, and the task has been assigned already.
    estateTaskInstance?.hasAssignButton &&
    estateTaskInstance?.status === EstateTaskInstanceStatus.Assigned
  ) {
    let label = estateTaskInstance?.assignButtonLabel;
    if (!label && estateTaskInstance.isSearch) {
      label = 'Run Search';
    }
    if (!label) {
      label = 'Yes';
    }
    return <AssignButton label={label} disabled />;
  } else if (
    // Show enabled button if the task has an assign button, and it isn't currently assigned.
    estateTaskInstance?.hasAssignButton &&
    estateTaskInstance?.status !== EstateTaskInstanceStatus.Assigned
  ) {
    let label = estateTaskInstance?.assignButtonLabel;
    if (!label && estateTaskInstance?.isSearch) {
      label = 'Run Search';
    }
    if (!label) {
      label = 'Yes';
    }
    return (
      <AssignButton
        locked={locked}
        label={label}
        onClick={() => onAssignClick!(estateTaskInstance!)}
      />
    );
  } else {
    return <></>;
  }
};

export default EstateStatusButton;
